import React, { useState, useEffect } from "react";
import { HiXCircle } from 'react-icons/hi';
import { Auth } from "../../../Common/lib/auth";

export default function PasswordStep({ userInfo, setUserInfo, currentStep, setCurrentStep, classNames }) {
  const [errors, setErrors] = useState({
    mainError: "",
    verifyCodeError: "",
    passwordError: "",
    secondPasswordError: "",
  });

  const requiredFields = ["verifyCode", "password", "secondPassword"];

  // Handles clearing errors when a user starts editing a field.
  useEffect(() => {
    for (let i = 0; i < requiredFields.length; i++) {
      const field = requiredFields[i];
      if (userInfo[field].length > 0) {
        setErrors(prevState => ({
          ...prevState,
          [`${field}Error`]: "",
        }));
      }
    }
  }, [userInfo]);

  /**
   * Submits the reset password request to Cognito. If successful, sends user to sign in page.
   */
  async function resetPassword() {
    setErrors(prevState => ({
      ...prevState,
      mainError: "",
    }));

    if (validateStep()) {
      try {
        const result = await Auth.forgotPasswordSubmit(userInfo.email, userInfo.verifyCode, userInfo.password);
        if (result.success) {
          setCurrentStep(currentStep + 1);
        }
      }
      catch (error) {
        // console.log(error.message);
       setErrors(prevState => ({
          ...prevState,
          mainError: "There was an error resetting your password.",
        }));
      }
    }
  }

  /**
   * Validates the current step of the sign up page.
   * @returns {boolean} Validation result. True if form is valid, false if form is invalid.
   */
  function validateStep() {
    let isValid = true;

    // Check if any of the fields are empty
    if (!requiredFields.every(field => String(userInfo[field]).trim() !== "")) {
      for (let i = 0; i < requiredFields.length; i++) {
        const field = requiredFields[i];
        if (String(userInfo[field]).trim() === "") {
          setErrors(prevState => ({
            ...prevState,
            [`${field}Error`]: "Required",
          }));
          isValid = false;
        }
      }
      return false;
    }

    // Check that the password is at least 8 characters in length
    if (userInfo.password.length < 8) {
      setErrors(prevState => ({
        ...prevState,
        passwordError: "Must be at least 8 characters long",
      }));
      return false;
    }

    // Check that the password doesn't start or end with a space character (Cognito doesn't allow it)
    if (!(/^[\S]+.*[\S]+$/).test(userInfo.password)) {
      setErrors(prevState => ({
        ...prevState,
        passwordError: "Cannot start or end with a space",
      }));
      isValid = false;
    }

    // Check that the two passwords match each other
    if (userInfo.secondPassword !== userInfo.password) {
      setErrors(prevState => ({
        ...prevState,
        secondPasswordError: "Passwords must match",
      }));
      isValid = false;
    }

    return isValid;
  }

  /**
   * Handles input field changes.
   * @param {*} field Name of userInfo property to change
   * @param {*} value Value from input field
   */
  function handleChange(field, value) {
    setUserInfo((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  }

  return (
    <>
      <div className="mb-6 sm:col-span-2">
        <label htmlFor="code" className="items-center flex text-sm sm:text-base font-medium text-gray-700 dark:text-white">
          Verification Code
          {errors.verifyCodeError.length > 0 &&
            <p className="italic text-xs sm:text-sm block font-base font-normal text-red-600 dark:text-red-500">
              &nbsp;- {errors.verifyCodeError}
            </p>
          }
        </label>
        <div className="mt-1">
          <input
            onChange={(e) => handleChange("verifyCode", e.target.value)}
            type="text"
            name="code"
            id="code"
            className={classNames(errors.verifyCodeError.length > 0 ? 'border-red-600 dark:border-red-500' : 'border-gray-300 dark:border-gray-600', 'border outline-none focus:ring-1 text-sm sm:text-base py-3 px-4 block w-full shadow-sm focus:ring-blue-500 focus:border-blue-500 rounded-md dark:bg-gray-700 dark:text-white')}
          />
        </div>
      </div>

      <div className="mb-6 sm:col-span-2">
        <label htmlFor="password" className="items-center flex text-sm sm:text-base font-medium text-gray-700 dark:text-white">
          New Password
          {errors.passwordError.length > 0 &&
            <p className="italic text-xs sm:text-sm block font-base font-normal text-red-600 dark:text-red-500">
              &nbsp;- {errors.passwordError}
            </p>
          }
        </label>
        <div className="mt-1">
          <input
            onChange={(e) => handleChange("password", e.target.value)}
            type="password"
            name="password"
            id="password"
            className={classNames(errors.passwordError.length > 0 ? 'border-red-600 dark:border-red-500' : 'border-gray-300 dark:border-gray-600', 'border outline-none focus:ring-1 text-sm sm:text-base py-3 px-4 block w-full shadow-sm focus:ring-blue-500 focus:border-blue-500 rounded-md dark:bg-gray-700 dark:text-white')}
          />
        </div>
      </div>

      <div className="sm:col-span-2">
        <label htmlFor="secondPassword" className="items-center flex text-sm sm:text-base font-medium text-gray-700 dark:text-white">
          Confirm Password
          {errors.secondPasswordError.length > 0 &&
            <p className="italic text-xs sm:text-sm block font-base font-normal text-red-600 dark:text-red-500">
              &nbsp;- {errors.secondPasswordError}
            </p>
          }
        </label>
        <div className="mt-1">
          <input
            onChange={(e) => handleChange("secondPassword", e.target.value)}
            type="password"
            name="secondPassword"
            id="secondPassword"
            className={classNames(errors.secondPasswordError.length > 0 ? 'border-red-600 dark:border-red-500' : 'border-gray-300 dark:border-gray-600', 'border outline-none focus:ring-1 text-sm sm:text-base py-3 px-4 block w-full shadow-sm focus:ring-blue-500 focus:border-blue-500 rounded-md dark:bg-gray-700 dark:text-white')}
          />
        </div>
      </div>

      {errors.mainError.length > 0 &&
        <div className="mt-1 rounded-md bg-red-100 dark:bg-red-900 p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <HiXCircle className="h-5 w-5 text-red-400" aria-hidden="true" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-400">{errors.mainError}</h3>
            </div>
          </div>
        </div>
      }

      <div className="mt-6 text-sm sm:text-base gap-14 sm:gap-24 flex justify-between">
        <button
          onClick={() => resetPassword()}
          type="button"
          className="rounded-md w-full bg-blue-600 py-2.5 px-3.5 text-md font-semibold text-white shadow-sm hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Reset Password
        </button>
      </div>
    </>
  )
}